import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { EyeVisible, Globe, Pencil } from '@factorialco/f0-react/icons/app'
import { ProcurementTypeItemFragment, WorkflowContainerFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'

export interface RequestTypeDetailHeaderProps {
  form: Form | undefined
  onPreview: () => void
  onPublish: () => void
  onVersioning: () => void
  workflow: WorkflowContainerFragment | null
  isWorkflowsEnabled: boolean
  requestType: ProcurementTypeItemFragment
  workflowIsDirty: boolean
  onSaveWorkflow: () => Promise<void>
}

export function RequestTypeDetailHeader({
  form,
  onPreview,
  onPublish,
  onVersioning,
  workflow,
  isWorkflowsEnabled,
  requestType,
  workflowIsDirty,
  onSaveWorkflow,
}: RequestTypeDetailHeaderProps) {
  const handlePublish = () => {
    onPublish()
  }

  const handleVersioning = async () => {
    onVersioning()
  }

  const formIsEmpty = form?.elements.length === 0
  const formIsDraft = form?.status === 'draft'
  const requestTypeEnabled = requestType.enabled

  const isSectionElement = (el: Element): el is SectionElement =>
    el.constructor.name === 'SectionElement'

  const formHasOnlyEmptySections = form?.elements.every((element) => {
    if (isSectionElement(element)) {
      return !element.elements || element.elements.length === 0
    }
    return false
  })

  const workflowIsDraft = workflow?.status === 'draft'

  const stateWhenWorkflowsEnabled = () => {
    const formIsPublishable = form ? !formIsEmpty && !formHasOnlyEmptySections : false
    const resourceIsDraft = formIsDraft || workflowIsDraft

    return {
      resourceIsDraft,
      shouldShowSaveButton: true as const,
      shouldShowPublishButton: !workflow || resourceIsDraft,
      isPublishEnabled: workflowIsDraft || formIsPublishable,
      isVersioningEnabled: Boolean(workflow && form),
      isSaveWorkflowEnabled: workflowIsDraft && workflowIsDirty,
    }
  }

  const stateWhenWorkflowsDisabled = () => {
    const formIsPublishable = form ? !formIsEmpty && !formHasOnlyEmptySections : false
    return {
      resourceIsDraft: formIsDraft,
      shouldShowSaveButton: false as const,
      shouldShowPublishButton: !form || formIsDraft,
      isPublishEnabled: formIsPublishable,
      isVersioningEnabled: Boolean(form),
      isSaveWorkflowEnabled: false,
    }
  }

  const {
    shouldShowPublishButton,
    shouldShowSaveButton,
    isPublishEnabled,
    isVersioningEnabled,
    isSaveWorkflowEnabled,
  } = isWorkflowsEnabled ? stateWhenWorkflowsEnabled() : stateWhenWorkflowsDisabled()

  const statusLabel = requestTypeEnabled
    ? i18n.t('procurement.types.forms.status.published')
    : i18n.t('procurement.types.forms.status.draft')

  return (
    <>
      <ResourceHeader
        title={form?.title || `${i18n.t('procurement.types.new_form')}`}
        primaryAction={
          shouldShowPublishButton
            ? {
                label: i18n.t('common.publish'),
                icon: Globe,
                onClick: handlePublish,
                disabled: !isPublishEnabled,
              }
            : {
                label: i18n.t('common.edit'),
                icon: Pencil,
                onClick: handleVersioning,
                disabled: !isVersioningEnabled,
              }
        }
        secondaryActions={[
          shouldShowSaveButton
            ? {
                label: i18n.t('common.save'),
                onClick: onSaveWorkflow,
                disabled: !isSaveWorkflowEnabled,
              }
            : null,
          {
            label: i18n.t('common.preview'),
            icon: EyeVisible,
            onClick: onPreview,
            disabled: false,
          },
        ].filter(Boolean)}
        status={{
          label: i18n.t('procurement.types.forms.status.label'),
          text: statusLabel,
          variant: requestTypeEnabled ? 'positive' : 'neutral',
        }}
      />
    </>
  )
}
